/**
 * Data hooks for the Partners admin screen — docs/modules/partnerLeads.model.md §3.
 * Same shape as `features/teams/useTeams.js`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as partnersApi from '../../api/partners.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/**
 * @param {object} [params] e.g. `{ q, partner_type, is_active }`
 * @param {boolean} [enabled] pass `false` when the caller doesn't hold
 *   `admin.partners:view` — e.g. the leads list's Partner filter, which
 *   would otherwise 403 for a rep just for having the filter block mounted.
 */
export function usePartners(params = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.partners.list(params),
    queryFn: () => partnersApi.listPartners(params),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function usePartner(id, enabled = true) {
  return useQuery({
    queryKey: queryKeys.partners.detail(id),
    queryFn: () => partnersApi.getPartner(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreatePartner() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => partnersApi.createPartner(payload),
    onSuccess: (partner) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
      dispatch(pushToast({ tone: 'success', message: `Partner "${partner.name}" registered.` }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not register that partner', detail: error.message }));
    },
  });
}

export function useUpdatePartner() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => partnersApi.updatePartner(id, patch, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
      dispatch(pushToast({ tone: 'success', message: 'Partner saved.' }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}
